<template>
  <SectionCard :id="section.id" :title="section.title">
    <div class="grid gap-6">
      <!-- Sökande -->
      <div>
        <h3 :id="section.subSections.applicants.id" class="mb-2">
          {{ section.subSections.applicants.title }}
        </h3>
        <template v-if="application.applicants?.length">
          <div class="applicant-list flex flex-col gap-3">
            <div
              v-for="applicant in application.applicants"
              :key="applicant.userId"
              class="applicant-card gap-4 rounded-lg p-4"
              :title="applicant.roles.isMainApplicant ? 'Huvudsökande' : 'Medsökande'"
            >
              <div class="applicant-identity">
                <h4 class="flex items-center gap-1 text-base">
                  <span v-if="applicant.roles.isMainApplicant">
                    <SvgIcon :path="mdiAccountStarOutline" :size="22" />
                  </span>
                  {{ getFullName(applicant) || '—' }}
                </h4>
                <p class="mt-0.5 text-subtle">
                  {{ applicant.personalNumber || '—' }}
                </p>
                <AppChip v-if="applicant.hasOpenKyc" color="warning" class="mt-2">Öppen KYC</AppChip>
              </div>

              <dl class="applicant-details gap-x-6 gap-y-3">
                <div v-for="detail in applicantDetails(applicant)" :key="detail.label">
                  <dt class="text-xs font-medium uppercase text-subtle">{{ detail.label }}</dt>
                  <dd class="mt-1">{{ detail.value }}</dd>
                </div>
              </dl>

              <div class="flex flex-wrap gap-2">
                <AppButton medium disabled title="Visa UC">
                  <template #prepend>
                    <SvgIcon :path="mdiFileChartOutline" :size="16" />
                  </template>
                  UC
                </AppButton>
              </div>
            </div>
          </div>
        </template>
        <p v-else class="text-subtle">Inga sökande.</p>
      </div>

      <div v-if="!isCommitmentApplication(application.type)">
        <h3 :id="section.subSections.documents.id" class="mb-1">
          {{ section.subSections.documents.title }}
        </h3>
        <AppTable class="mt-4">
          <thead>
            <tr>
              <th>Dokument</th>
              <th>Tillhör</th>
              <th>Skapat</th>
              <th></th>
            </tr>
          </thead>
          <tbody v-if="incomeDocuments?.length">
            <AppTableRow v-for="item in incomeDocuments" :key="item.id" class="document-row">
              <AppTableCell>{{ translateDocumentType(item.type) }}</AppTableCell>
              <AppTableCell>{{ getDocumentRelation(application.applicants, item.relations) }}</AppTableCell>
              <AppTableCell nowrap>{{ formatDateIfValid(item.createdAt) }}</AppTableCell>
              <AppTableCell action>
                <AppButton
                  icon
                  small
                  :loading="openingDocumentId === item.id"
                  :aria-label="`Visa ${translateDocumentType(item.type)}`"
                  title="Visa"
                  @click="openDocument(item)"
                >
                  <SvgIcon :path="mdiMagnify" size="18" />
                </AppButton>
              </AppTableCell>
            </AppTableRow>
          </tbody>

          <tbody v-else>
            <AppTableRow>
              <AppTableCell colspan="4" class="text-center">Inga inkomstunderlag.</AppTableCell>
            </AppTableRow>
          </tbody>
        </AppTable>
      </div>
    </div>
  </SectionCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiAccountStarOutline, mdiFileChartOutline, mdiMagnify } from '@mdi/js'
import { formatAmountWithCurrency, formatDateIfValid } from '@staff/shared'
import { getFullName, isCommitmentApplication } from '@/utils/applicationDisplay'
import { AppButton, AppChip, AppTable, AppTableCell, AppTableRow, SectionCard, SvgIcon } from '@staff/ui'
import { useRequiredApplication } from '@/views/application/application.context'

import type { ApplicationSections } from './sections'
import { useMetadata } from '@/composables/useMetadata'
import {
  useApplicationDocumentsContext,
  getDocumentsByType,
  getDocumentRelation,
} from '@/views/application/applicationDocuments.context'
import { useDocumentPreview } from '@/composables/documentPreview'

const MISSING_VALUE = '—'

defineProps<{
  section: ApplicationSections['repaymentCapacity']
}>()

const application = useRequiredApplication()

const { translateDocumentType, getReferenceValues } = useMetadata()
const { documents } = useApplicationDocumentsContext()

const { openingDocumentId, openDocument } = useDocumentPreview((document) => translateDocumentType(document.type))

type Applicant = (typeof application.value.applicants)[number]

const referenceLabel = (referenceKey: string, code: string | null) => {
  if (!code) return MISSING_VALUE
  return getReferenceValues(referenceKey).find((item) => item.code === code)?.label ?? code
}

const applicantDetails = (applicant: Applicant) => {
  const employmentInfo = applicant.employmentInfo

  return [
    {
      label: 'Månadsinkomst',
      value: formatAmountWithCurrency(applicant.monthlyIncome, MISSING_VALUE),
    },
    ...(employmentInfo
      ? [
          { label: 'Anställningsform', value: referenceLabel('employmentStatuses', employmentInfo.employmentStatus) },
          ...(employmentInfo.employer ? [{ label: 'Arbetsgivare', value: employmentInfo.employer }] : []),
          ...(employmentInfo.employedStartDate
            ? [{ label: 'Anställd sedan', value: formatDateIfValid(employmentInfo.employedStartDate) }]
            : []),
          ...(employmentInfo.temporaryEmployedEndDate
            ? [{ label: 'Visstid t.o.m.', value: formatDateIfValid(employmentInfo.temporaryEmployedEndDate) }]
            : []),
          ...(employmentInfo.employerOrganisationNumber
            ? [{ label: 'Org.nr arbetsgivare', value: employmentInfo.employerOrganisationNumber }]
            : []),
        ]
      : []),
    { label: 'Civilstånd', value: referenceLabel('maritalStatuses', applicant.maritalStatus) },
    { label: 'E-post', value: applicant.contactDetails.email ?? MISSING_VALUE },
    { label: 'Telefon', value: applicant.contactDetails.phoneNumber ?? MISSING_VALUE },
    { label: 'Adress', value: applicant.address ?? MISSING_VALUE },
  ]
}

const incomeDocuments = computed(() => getDocumentsByType(documents.value, 'monthlyIncome'))
</script>

<style scoped lang="scss">
.applicant-list {
  container-type: inline-size;
}

.applicant-card {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border-muted);
  background-color: var(--color-canvas-inset);
}

.applicant-details {
  display: grid;
  grid-template-columns: 1fr;
}

@container (min-width: 28rem) {
  .applicant-details {
    grid-template-columns: 1fr 1fr;
  }
}

@container (min-width: 42rem) {
  .applicant-card {
    flex-direction: row;
    align-items: flex-start;
  }

  .applicant-identity {
    width: 14rem;
    flex-shrink: 0;
  }

  .applicant-details {
    flex: 1;
  }
}

@container (min-width: 60rem) {
  .applicant-details {
    grid-template-columns: repeat(3, 1fr);
  }
}

.document-row:nth-child(even) {
  background-color: var(--color-canvas-inset);
}
</style>
